import { Button } from '../button/button';
import { useEffect, useRef, useState } from "react";
import { Loader } from "../loader/loader";
import { useTranslation } from 'react-i18next';
import HomePageStyling from "../../models/interfaces/home-page-styling";
import { HexColorPicker } from "react-colorful";
import { useDropzone } from 'react-dropzone';
import { TextTypeInput } from "../forms";
import { useForm } from "react-hook-form";
import AppContentUtil from "../../utilities/app-content-util";
import logoPlaceholder from "../../assets/images/logo-placeholder.png"
import videoPlaceholder from "../../assets/images/video-placeholder.png"
import imagePlaceholder from "../../assets/images/image-placeholder.png"
import assessmentResultsBody from "../../assets/images/assessment-results-body.png"
import CancelIcon from '@mui/icons-material/Cancel';
import { Utils } from "../../utilities/utils";

export interface AssessmentResultsHeaderStylingProps {
    onSave: (assessmentResultsHeaderStyling: HomePageStyling, file?: File) => Promise<void>;
    homePageStyling?: HomePageStyling;
}

const COMPONENT_CLASS = "c-assessment-results-header-styling";
const COMPONENT_CLASS_ASSESSMENT_RESULTS = "c-assessment-results";

const AssessmentResultsHeaderStyling: React.FunctionComponent<AssessmentResultsHeaderStylingProps> = (props) => {
    const {
        homePageStyling,
        onSave,
    } = props;

    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);

    const defaultBackgroundColor = "#10151B";
    const defaultButtonColor = "#F3F3F4";
    const defaultTextColor = "#FFFFFF";
    const defaultButtonTextColor = "#121622";

    const [logoUrl, setLogoUrl] = useState(homePageStyling?.logoUrl);
    const [fileName, setFileName] = useState(homePageStyling?.logoUrl ? t('assessmentResults.headerStyling.uploadedLogo') : undefined);
    const [needFileUpload, setNeedFileUpload] = useState(false);
    const [videoUrl, setVideoUrl] = useState(homePageStyling?.videoUrl);
    const [videoEmbed, setVideoEmbed] = useState(AppContentUtil.getVideoEmbedCode(homePageStyling?.videoUrl));
    const [backgroundColor, setBackgroundColor] = useState(homePageStyling?.backgroundColor);
    const [headerText, setHeaderText] = useState(homePageStyling?.headerText);
    const [headerTextCharCount, setHeaderTextCharCount] = useState(homePageStyling?.headerText?.length || 0);
    const [paragraphText, setParagraphText] = useState(homePageStyling?.paragraphText);
    const [paragraphTextCharCount, setParagraphTextCharCount] = useState(homePageStyling?.paragraphText?.length || 0);
    const [textColor, setTextColor] = useState(homePageStyling?.textColor);
    const [buttonText, setButtonText] = useState(homePageStyling?.buttonText);
    const [buttonUrl, setButtonUrl] = useState(homePageStyling?.buttonUrl);
    const [buttonColor, setButtonColor] = useState(homePageStyling?.buttonColor);
    const [buttonTextColor, setButtonTextColor] = useState(homePageStyling?.buttonTextColor);
    const [showColorPickerBackground, setShowColorPickerBackground] = useState(false);
    const [showColorPickerText, setShowColorPickerText] = useState(false);
    const [showColorPickerButton, setShowColorPickerButton] = useState(false);
    const [showColorPickerButtonText, setShowColorPickerButtonText] = useState(false);

    const headerTextMaxLength = 60;
    const paragraphTextMaxLength = 300;

    const backgroundPickerRef = useRef<HTMLDivElement>(null);
    const textPickerRef = useRef<HTMLDivElement>(null);
    const buttonPickerRef = useRef<HTMLDivElement>(null);
    const buttonTextPickerRef = useRef<HTMLDivElement>(null);

    const methods = useForm<any>();
    const {
        register,
    } = methods;

    const fileSizeValidator = (file: File) => {
        const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2,097,152 bytes

        if (file.size > MAX_FILE_SIZE) {
            return {
                code: "file-too-large",
                message: `File is larger than ${MAX_FILE_SIZE / 1024 / 1024} MB`
            };
        }

        return null
    }

    const {
        acceptedFiles,
        getRootProps,
        getInputProps,
        open,
    } = useDropzone({
        accept: {
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/svg+xml': ['.svg'],
        },
        validator: fileSizeValidator,
        maxFiles: 1,
        noClick: true,
        noKeyboard: true,
        onDrop: (acceptedFiles) => {
            const tempUrl = URL.createObjectURL(acceptedFiles[0]);
            setFileName(acceptedFiles[0].name);
            setLogoUrl(tempUrl);
            setNeedFileUpload(true);
        }
    });

    const removeFile = () => {
        if (logoUrl && logoUrl.startsWith("blob:")) {
            URL.revokeObjectURL(logoUrl);
        }
        setLogoUrl("");
        setFileName("");
        setNeedFileUpload(false);
    };

    const validateColor = (color: string) => {
        // If user deletes everything, or removes the hash, add it back
        if (!color.startsWith("#")) {
            color = "#" + color.replace(/#/g, ""); // Remove any duplicate # and prepend one
        }

        return color;
    };

    const handleSave = async () => {
        setIsLoading(true);

        const styling: HomePageStyling = {
            logoUrl: logoUrl ?? "",
            videoUrl: videoUrl ?? "",
            backgroundColor: Utils.isValidHexColor(backgroundColor) ? backgroundColor : "",
            headerText: headerText ?? "",
            paragraphText: paragraphText ?? "",
            textColor: Utils.isValidHexColor(textColor) ? textColor : "",
            buttonText: buttonText ?? "",
            buttonUrl: buttonUrl ?? "",
            buttonColor: Utils.isValidHexColor(buttonColor) ? buttonColor : "",
            buttonTextColor: Utils.isValidHexColor(buttonTextColor) ? buttonTextColor : "",
        };

        if (needFileUpload && acceptedFiles.length > 0) {
            await onSave(styling, acceptedFiles[0]);
        }
        else {
            await onSave(styling);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (showColorPickerBackground && backgroundPickerRef.current && !backgroundPickerRef.current.contains(target)) {
                setShowColorPickerBackground(false);
            }

            if (showColorPickerText && textPickerRef.current && !textPickerRef.current.contains(target)) {
                setShowColorPickerText(false);
            }

            if (showColorPickerButton && buttonPickerRef.current && !buttonPickerRef.current.contains(target)) {
                setShowColorPickerButton(false);
            }

            if (showColorPickerButtonText && buttonTextPickerRef.current && !buttonTextPickerRef.current.contains(target)) {
                setShowColorPickerButtonText(false);
            }
        };

        if (showColorPickerBackground || showColorPickerText || showColorPickerButton || showColorPickerButtonText) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showColorPickerBackground, showColorPickerButton, showColorPickerButtonText, showColorPickerText]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            setVideoEmbed(AppContentUtil.getVideoEmbedCode(videoUrl));
        }, 800);

        return () => clearTimeout(debounce);
    }, [videoUrl]);

    const header = (
        <div
            className="text-white py-11 px-4 md:px-8 mx-auto"
            style={{
                backgroundColor: backgroundColor && backgroundColor.length > 1 ? backgroundColor : defaultBackgroundColor,
                borderRadius: "40px 40px 0 0",
                maxWidth: "500px", minWidth: "500px", width: "500px",
            }}
        >
            <div className="h-7">
                <img
                    className="max-h-full"
                    src={logoUrl || logoPlaceholder}
                    alt="Logo"
                    onLoad={() => {
                        if (logoUrl && logoUrl.startsWith("blob:")) {
                            URL.revokeObjectURL(logoUrl);
                        }
                    }} // Cleanup memory
                />
            </div>
            <div className="flex flex-wrap justify-between mt-4 items-center gap-4">
                <div className={`${COMPONENT_CLASS_ASSESSMENT_RESULTS}__header-companion`}>
                    <div className="text-white font-light" style={{ color: textColor || "#FFFFFF", fontSize: "13px", lineHeight: "17px", marginBottom: "10px" }}>{headerText || t('general.placeholder.loremIpsumHeader')}</div>
                    <p className="font-light" style={{ color: textColor && textColor.length > 1 ? textColor : defaultTextColor || "#FFFFFF", fontSize: "7px", lineHeight: "11px", marginBottom: "10px" }}>
                        {paragraphText || t('general.placeholder.loremIpsumParagraph')}
                    </p>
                    <div className="flex justify-center md:justify-start">
                        <Button
                            type="default"
                            buttonText={buttonText || t('assessmentResults.headerStyling.buttonText')}
                            style={{ backgroundColor: buttonColor && buttonColor.length > 1 ? buttonColor : defaultButtonColor, color: buttonTextColor && buttonTextColor.length > 1 ? buttonTextColor : defaultButtonTextColor, fontSize: "7px", minHeight: "22px", height: "22px", padding: "0 12px" }}
                            onClick={() => window.open(buttonUrl, "_blank")}
                        />
                    </div>
                </div>
                <div className={`${COMPONENT_CLASS}__video-companion`}>
                    {videoEmbed
                        ?
                        <div className={`${COMPONENT_CLASS}__video-wrapper`} dangerouslySetInnerHTML={{ __html: videoEmbed }}></div>
                        :
                        <img src={videoPlaceholder} alt="Symmio Mobile App" />
                    }
                </div>
            </div>
        </div>
    );

    const colorPickerElement = (
        showPicker: boolean,
        setShowPicker: React.Dispatch<React.SetStateAction<boolean>>,
        color: string | undefined,
        setColor: React.Dispatch<React.SetStateAction<string | undefined>>,
        id: string,
        label: string,
        pickerRef: React.RefObject<HTMLDivElement>
    ) => (
        <div className={`${COMPONENT_CLASS}__color-picker-section`}>
            <div ref={pickerRef} className={`${COMPONENT_CLASS}__color-picker ${showPicker ? "block" : "hidden"}`}>
                <HexColorPicker key={id} color={color} onChange={setColor} />
            </div>
            <label htmlFor={id}>{label}</label>
            <div className="flex items-center gap-2">
                <div className="rounded-full w-9 h-9 border" style={{ backgroundColor: color }}>
                    <button
                        onClick={() => setShowPicker((prev) => !prev)}
                        className="w-9 h-9" />
                </div>
                <div className={`${COMPONENT_CLASS}__field`}>
                    <TextTypeInput
                        id={id}
                        type="text"
                        registerHook={register}
                        label={label}
                        hideLabel={true}
                        registerOptions={{
                            value: color,
                            onChange: (e) => {
                                setColor(validateColor(e.target.value));
                            },
                            maxLength: 7,
                        }}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className={`${COMPONENT_CLASS}`}>
            {isLoading &&
                <Loader isVisible={isLoading} />
            }

            <div className="flex flex-wrap gap-4">
                <div className="bg-black-gray-5 p-10 rounded-xl" style={{ width: "49%" }}>
                    <div>
                        <h2 className="mb-4">{t('assessmentResults.headerStyling.logo')}</h2>
                        <div {...getRootProps({ className: `${COMPONENT_CLASS}__dropzone` })}>
                            <input {...getInputProps()} />
                            <div className="flex w-full gap-4 items-center">
                                <div className="flex w-48 h-20 px-2.5 py-5 justify-center items-center border border-black-gray-5 rounded-xl basis-[12rem] flex-shrink-0">
                                    <img
                                        className="max-h-11"
                                        src={logoUrl || imagePlaceholder}
                                        alt="Logo"
                                        onLoad={() => {
                                            if (logoUrl && logoUrl.startsWith("blob:")) {
                                                URL.revokeObjectURL(logoUrl);
                                            }
                                        }} // Cleanup memory
                                    />
                                </div>
                                {logoUrl && fileName
                                    ?
                                    <div className="flex flex-nowrap gap-4 justify-between flex-1 min-w-0">
                                        <span className="overflow-ellipsis overflow-hidden whitespace-nowrap min-w-0 flex-1">{fileName}</span>
                                        <button
                                            type="button"
                                            onClick={removeFile}
                                            aria-label="Remove Image"
                                            className="flex-shrink-0"
                                        >
                                            <CancelIcon
                                                style={{
                                                    color: '#121622',
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%',
                                                }}
                                            />
                                        </button>
                                    </div>
                                    :
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-light whitespace-pre-line" onClick={open} dangerouslySetInnerHTML={{ __html: t('assessmentResults.headerStyling.dropzoneText') }}></p>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>

                    <hr />

                    <div>
                        <h2 className="mb-4">{t('assessmentResults.headerStyling.background')}</h2>
                        {colorPickerElement(showColorPickerBackground, setShowColorPickerBackground, backgroundColor, setBackgroundColor, "background-color", t('assessmentResults.headerStyling.backgroundColor'), backgroundPickerRef)}
                    </div>

                    <hr />

                    <div>
                        <h2 className="mb-4">{t('assessmentResults.headerStyling.video')}</h2>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <TextTypeInput
                                id="video-url"
                                type="text"
                                registerHook={register}
                                label={t('assessmentResults.headerStyling.videoUrl')}
                                registerOptions={{
                                    value: videoUrl,
                                    onChange: (e) => setVideoUrl(e.target.value),
                                }} />
                        </div>
                    </div>

                    <hr />

                    <div>
                        <h2 className="mb-4">{t('assessmentResults.headerStyling.text')}</h2>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <TextTypeInput
                                id="header-text"
                                type="text"
                                registerHook={register}
                                label={t('assessmentResults.headerStyling.headerText')}
                                labelRight={t('assessmentResults.headerStyling.charCount', { charCount: headerTextCharCount, maxLength: headerTextMaxLength })}
                                labelRightClassName={`${COMPONENT_CLASS}__input-label-right ${headerTextCharCount / headerTextMaxLength > 0.9 ? "text-red-dark" : ""}`}
                                registerOptions={{
                                    value: headerText,
                                    onChange: (e) => {
                                        setHeaderText(e.target.value);
                                        setHeaderTextCharCount(e.target.value.length);
                                    },
                                    maxLength: headerTextMaxLength,
                                }} />
                        </div>

                        <div className={`${COMPONENT_CLASS}__field`}>
                            <TextTypeInput
                                id="paragraph-text"
                                inputClassName="textarea"
                                type="textarea"
                                registerHook={register}
                                label={t('assessmentResults.headerStyling.paragraphText')}
                                labelRight={t('assessmentResults.headerStyling.charCount', { charCount: paragraphTextCharCount, maxLength: paragraphTextMaxLength })}
                                labelRightClassName={`${COMPONENT_CLASS}__input-label-right ${paragraphTextCharCount / paragraphTextMaxLength > 0.9 ? "text-red-dark" : ""}`}
                                registerOptions={{
                                    value: paragraphText,
                                    onChange: (e) => {
                                        setParagraphText(e.target.value);
                                        setParagraphTextCharCount(e.target.value.length);
                                    },
                                    maxLength: paragraphTextMaxLength,
                                }} />
                        </div>

                        {colorPickerElement(showColorPickerText, setShowColorPickerText, textColor, setTextColor, "text-color", t('assessmentResults.headerStyling.textColor'), textPickerRef)}
                    </div>

                    <hr />

                    <div>
                        <h2 className="mb-4">{t('assessmentResults.headerStyling.button')}</h2>
                        <div className={`${COMPONENT_CLASS}__field`}>
                            <TextTypeInput
                                id="button-text"
                                type="text"
                                registerHook={register}
                                label={t('assessmentResults.headerStyling.buttonText')}
                                registerOptions={{
                                    value: buttonText,
                                    onChange: (e) => setButtonText(e.target.value),
                                }} />
                        </div>

                        <div className={`${COMPONENT_CLASS}__field`}>
                            <TextTypeInput
                                id="button-url"
                                type="text"
                                registerHook={register}
                                label={t('assessmentResults.headerStyling.buttonUrl')}
                                registerOptions={{
                                    value: buttonUrl,
                                    onChange: (e) => setButtonUrl(e.target.value),
                                    onBlur: (e) => setButtonUrl(Utils.validateUrl(e.target.value)),
                                }} />
                        </div>

                        {colorPickerElement(showColorPickerButton, setShowColorPickerButton, buttonColor, setButtonColor, "button-color", t('assessmentResults.headerStyling.buttonColor'), buttonPickerRef)}

                        {colorPickerElement(showColorPickerButtonText, setShowColorPickerButtonText, buttonTextColor, setButtonTextColor, "button-text-color", t('assessmentResults.headerStyling.buttonTextColor'), buttonTextPickerRef)}
                    </div>
                </div>

                <div className="bg-black-gray-5 p-10 rounded-xl" style={{ width: "49%", minWidth: "fit-content" }}>
                    {header}
                    <div
                        className="mx-auto"
                        style={{
                            boxShadow: "5px 5px 20px 0px rgba(0, 0, 0, 0.05)",
                            borderRadius: "0 0 40px 40px",
                            maxWidth: "500px", minWidth: "500px", width: "500px",
                        }}>
                        <img src={assessmentResultsBody} alt="Assessment Results Body" />
                    </div>
                </div>
            </div>

            <div className="mt-10">
                <Button
                    type="default"
                    buttonText={t('buttons.btn_saveChanges')}
                    onClick={handleSave}
                />
            </div>
        </div>
    );
};

export default AssessmentResultsHeaderStyling;